import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import type { PersonaOpportunityDisplayRow } from "../../../lib/persona-opportunity-row-model.js";
import { OpportunityHubStatusBadge } from "../../page/PersonaOpportunityHubChrome/OpportunityHubStatusBadge.js";
import { PersonaOpportunitySupportingMedia } from "../../shared/PersonaOpportunitySupportingMedia/PersonaOpportunitySupportingMedia.js";
import { PersonaOpportunitySelectCheckbox } from "../../shared/PersonaOpportunitySelectCheckbox/PersonaOpportunitySelectCheckbox.js";

import {
  PersonaOpportunityEvidenceStrip,
  PersonaOpportunityInsight,
} from "./PersonaOpportunityInsight.js";

export interface PersonaOpportunityCardProps {
  row: PersonaOpportunityDisplayRow;
  selected?: boolean;
  selectionEnabled?: boolean;
  onToggleSelect?: (opportunityId: string) => void;
  /** When set, Generate scripts is enabled and opens that flow for this card. */
  onGenerateScripts?: (opportunityId: string) => void;
}

export function PersonaOpportunityCard({
  row,
  selected = false,
  selectionEnabled = false,
  onToggleSelect,
  onGenerateScripts,
}: PersonaOpportunityCardProps) {
  const { t } = useTranslation();
  const notReady = t(
    "personaOpportunities.workspace.actionNotReady",
    "Not available yet — coming in a later release.",
  );
  const generateScriptsLabel = t("personaOpportunities.active.generateScripts", "Generate scripts");

  return (
    <article className="flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-xs transition-shadow hover:shadow-md">
      <div className="flex flex-col gap-5 p-5 pb-4 sm:flex-row sm:items-stretch">
        <div className="relative mx-auto w-full max-w-[200px] shrink-0 sm:mx-0 sm:w-[164px] sm:self-stretch">
          <PersonaOpportunitySupportingMedia
            row={row}
            variant="card"
            className="rounded-[10px] sm:h-full sm:min-h-[300px] sm:aspect-auto"
          >
            <div className="pointer-events-none absolute inset-x-2.5 top-2.5 z-[3] flex items-center justify-between gap-2">
              <span className="rounded-md bg-foreground/75 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-background">
                {t("personaOpportunities.active.rankBadge", {
                  rank: row.rank,
                  defaultValue: "#{{rank}}",
                })}
              </span>
              <OpportunityHubStatusBadge status={row.status} />
            </div>
          </PersonaOpportunitySupportingMedia>
        </div>

        <PersonaOpportunityInsight
          row={row}
          variant="card"
          headerAction={
            row.postFlight ? null : (
              <PersonaOpportunitySelectCheckbox
                className="-my-3 -mr-3 [@media(hover:hover)_and_(pointer:fine)]:m-0"
                checked={selected}
                disabled={!selectionEnabled}
                onToggle={() => onToggleSelect?.(row.id)}
              />
            )
          }
        />
      </div>

      {row.fromLabel || row.postFlight ? null : (
        <PersonaOpportunityEvidenceStrip row={row} className="mx-5 mb-4" />
      )}

      {row.postFlight ? null : (
      <TooltipProvider delayDuration={200}>
        <div className="flex items-center justify-between gap-3 border-t border-border bg-muted/40 px-5 py-3.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button type="button" variant="outline" size="sm" iconStart="layout-grid" disabled>
                  {t("personaOpportunities.active.similarAngles", "See similar angles")}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{notReady}</TooltipContent>
          </Tooltip>
          {onGenerateScripts ? (
            <Button
              type="button"
              size="sm"
              iconStart="sparkles"
              iconEnd="arrow-right"
              onClick={(event) => {
                event.stopPropagation();
                onGenerateScripts(row.id);
              }}
            >
              {generateScriptsLabel}
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button type="button" size="sm" iconStart="sparkles" iconEnd="arrow-right" disabled>
                    {generateScriptsLabel}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{notReady}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </TooltipProvider>
      )}
    </article>
  );
}
